import { useRef, useState } from 'react';

import toast from 'react-hot-toast';

import { gql } from '@apollo/client';

import FileReportTable, { FileReportTableFragments } from '../FileReportTable';
import { TapestryReviewCardFile } from './__generated__/TapestryReviewCard';
import { RoleGatedButton } from './RoleGatedButton';
import { extractTapestryErrorCode, useTapestryJobActions } from './useTapestryJobActions';

export const TapestryReviewCardFragments = {
  loanTape: gql`
    fragment TapestryReviewCardFile on File {
      id
      processing_report {
        ...FileReportTableProcessingReport
      }
    }
    ${FileReportTableFragments.processingReport}
  `,
};

// api error code → user-facing message for the confirm action. Unknown codes
// fall back to a generic message.
const CONFIRM_ERROR_MESSAGES: Record<string, string> = {
  'E-4093': 'This tape isn’t ready to save yet. Give it a moment and try again.',
  'E-4091': 'This tape is no longer awaiting review — refresh to see its status.',
  'E-4030': 'You don’t have permission to save this tape.',
};

interface Props {
  selectedFile: TapestryReviewCardFile;
  /** Called after confirm/reject resolves so the caller can refetch job status. */
  onResolved: () => void | Promise<unknown>;
}

/**
 * The Tapestry review gate (current_stage === WAITING_FOR_REVIEW). Shows the
 * extraction report and lets a portfolio or deal manager Confirm (persist the loans) or
 * Reject (abort). It never calls the legacy `ingestFile`. The terminal result
 * (success / success_no_persist / error) is rendered by the outcome panel
 * (ENG-2794) once the run completes; here we just resolve the gate and refetch.
 */
export const TapestryReviewCard = ({ selectedFile, onResolved }: Props): JSX.Element => {
  const { confirm, reject, loading } = useTapestryJobActions();

  // Latches true on the first successful confirm/reject and keeps the buttons
  // disabled until this card unmounts (it disappears once the refetch shows the
  // gate resolved). Apollo's `loading` clears before the parent's refetch
  // updates `ready_for_review`, so without this a second confirm/reject — or the
  // opposing action — could fire in that window.
  const [resolved, setResolved] = useState(false);
  const disabled = loading || resolved;

  // Synchronous re-entry guard. `disabled` (loading || resolved) only blocks the
  // buttons on the NEXT render — Apollo's `loading` and the `resolved` latch both
  // flip after the current tick — so a double-click, or Confirm-then-Reject fired
  // in the same tick, could dispatch overlapping mutations before either button
  // visibly disables. This ref is set the instant an action starts and shared by
  // both handlers, so the second call returns immediately. Reset only on error
  // (so a failed action can be retried); a success keeps it latched alongside
  // `resolved` since the two actions are mutually exclusive and irreversible.
  const actionInFlight = useRef(false);

  // Fire the parent's status refresh without ever surfacing an unhandled
  // rejection: onResolved is `refetchTapestryJob`, which rejects on a network
  // blip. A lost refresh is reconciled by TapeUploadFlow's resume-after-gate
  // backstop (it re-kicks the refetch off the 5s File poll) and ultimately a
  // reload, so it's safe to swallow. Used by BOTH the success and the
  // error/resync paths so neither leaks a rejection.
  const resync = () => {
    Promise.resolve(onResolved()).catch(() => {
      /* swallow: backstop + reload reconcile a failed refresh */
    });
  };

  // Once a confirm/reject mutation succeeds the gate is resolved for good: the
  // two actions are mutually exclusive and irreversible, so keep BOTH buttons
  // latched even if the status refetch fails. Re-enabling would let the user
  // fire the opposite action (e.g. Reject after Confirm already started
  // persisting); a failed refetch is reconciled by the next poll / page reload.
  const settle = () => {
    setResolved(true);
    resync();
  };

  const onConfirm = async () => {
    if (actionInFlight.current || resolved) return;
    actionInFlight.current = true;
    try {
      await confirm(selectedFile.id);
    } catch (error) {
      actionInFlight.current = false;
      const code = extractTapestryErrorCode(error) ?? '';
      toast.error(CONFIRM_ERROR_MESSAGES[code] ?? 'Could not save this tape. Please try again.');
      // Re-sync: codes like E-4091 mean the gate already moved on, so refetch so
      // the (paused) job status reflects reality instead of the stale cache.
      resync();
      return;
    }
    settle();
  };

  const onReject = async () => {
    if (actionInFlight.current || resolved) return;
    actionInFlight.current = true;
    try {
      await reject(selectedFile.id);
    } catch (error) {
      actionInFlight.current = false;
      const code = extractTapestryErrorCode(error) ?? '';
      toast.error(
        code === 'E-4030'
          ? 'You don’t have permission to reject this tape.'
          : 'Could not reject this tape. Please try again.',
      );
      resync();
      return;
    }
    settle();
  };

  return (
    <div
      className="rounded border border-accent-muted bg-background-canvas p-5 text-sm"
      data-testid="tapestry-review-card"
    >
      <h2 className="mb-2.5 text-lg text-gray-600">Review extracted loans</h2>
      <p className="my-1 text-xs text-gray-500">
        Processing is complete. Review the summary below, then confirm to save these loans to your
        portfolio or reject to discard them.
      </p>
      <div className="mt-3">
        {/* The gate can mount (driven by the job's ready_for_review) a poll
            before the 5s GetLoanTape catches up, so processing_report may be
            null briefly — FileReportTable dereferences it, so guard rather than
            crash the detail page. */}
        {selectedFile.processing_report ? (
          <FileReportTable processingReport={selectedFile.processing_report} />
        ) : (
          <p className="text-xs text-gray-500" data-testid="tapestry-review-report-pending">
            Loading the extraction summary…
          </p>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-3 px-3 py-3">
        <RoleGatedButton
          label="Reject"
          type="secondary"
          data-testid="tapestry-reject-button"
          disabled={disabled}
          onClick={() => void onReject()}
        >
          Reject
        </RoleGatedButton>
        <RoleGatedButton
          label="Confirm & save"
          data-testid="tapestry-confirm-button"
          disabled={disabled}
          onClick={() => void onConfirm()}
        >
          Confirm &amp; save
        </RoleGatedButton>
      </div>
    </div>
  );
};
